import React from "react";
import {
  Container,
  Stack,
  Typography,
  useTheme,
  Box,
  IconButton,
  Paper,
  Link,
  Grid,
} from "@mui/material";
import { motion } from "framer-motion";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import { Instagram } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TelegramIcon from "@mui/icons-material/Telegram";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

export default function ContactMe() {
  const theme = useTheme();

  const socialLinks = [
    {
      icon: <LinkedInIcon fontSize="medium" />,
      label: "LinkedIn",
      color: theme.palette.primary.main,
      url: "https://linkedin.com/in/pouriya3a",
    },
    {
      icon: <GitHubIcon fontSize="medium" />,
      label: "GitHub",
      color: theme.palette.text.primary,
      url: "https://github.com/pouriya-3a",
    },
    {
      icon: <Instagram fontSize="medium" />,
      label: "Instagram",
      color: "#E1306C",
      url: "https://instagram.com/pouriya_3a",
    },
    {
      icon: <TelegramIcon fontSize="medium" />,
      label: "Telegram",
      color: "#0088cc",
      url: "https://t.me/pouriya_3a",
    },
  ];

  const contactInfo = [
    {
      icon: <EmailIcon />,
      title: "Email",
      value: "pouriyasabouri1385@gmail.com",
      color: theme.palette.primary.main,
      href: "mailto:pouriyasabouri1385@gmail.com",
    },
    {
      icon: <PhoneIcon />,
      title: "Phone",
      value: "+98 915 686 2623",
      color: theme.palette.secondary.main,
      href: "tel:+989156862623",
    },
    {
      icon: <LocationOnIcon />,
      title: "Location",
      value: "Mashhad, Khorasan Razavi, Iran",
      color: theme.palette.info.main,
      href: "https://maps.google.com/?q=Mashhad,Iran",
    },
  ];

  // Map variables
  const contactItems = contactInfo?.map((item, index) => (
    <motion.div
      key={index}
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Link
        href={item?.href}
        target="_blank"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          p: 2.5,
          borderRadius: 4,
          bgcolor: "rgba(255, 255, 255, 0.02)",
          border: "1px solid rgba(255, 255, 255, 0.05)",
          textDecoration: "none",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateX(8px)",
            bgcolor: "rgba(255, 255, 255, 0.04)",
            borderColor: `${item.color}50`,
            boxShadow: `-4px 0 20px -5px ${item.color}30`,
            "& .icon-box": {
              bgcolor: item.color,
              color: "#fff",
              boxShadow: `0 0 15px ${item.color}80`,
            },
          },
        }}
      >
        <Box
          className="icon-box"
          sx={{
            width: 50,
            height: 50,
            borderRadius: 3,
            bgcolor: `${item.color}15`,
            color: item.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s ease",
          }}
        >
          {item.icon}
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            {item.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.primary", fontWeight: 600, mt: 0.5 }}
          >
            {item.value}
          </Typography>
        </Box>
      </Link>
    </motion.div>
  ));

  const socialItems = socialLinks?.map((social, index) => (
    <motion.div
      key={index}
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
    >
      <IconButton
        href={social.url}
        target="_blank"
        aria-label={social.label}
        sx={{
          width: 60,
          height: 60,
          borderRadius: 4,
          bgcolor: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          color: "text.secondary",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            bgcolor: `${social.color}15`,
            color: social.color,
            transform: "translateY(-6px) scale(1.05)",
            borderColor: `${social.color}50`,
            boxShadow: `0 10px 25px -5px ${social.color}50`,
          },
          "&:active": {
            transform: "scale(0.95)",
          },
        }}
      >
        {social.icon}
      </IconButton>
    </motion.div>
  ));

  return (
    <Box
      id="contact-section"
      sx={{
        position: "relative",
        py: { xs: 8, md: 12 },
        overflow: "hidden", // Prevents glowing orbs from causing scrollbars
      }}
    >
      {/* Animated Ambient Background Elements */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {/* Primary Glowing Orb */}
        <motion.div
          animate={{
            x: [0, 80, -40, 0],
            y: [0, -60, 40, 0],
            scale: [1, 1.2, 0.9, 1],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            position: "absolute",
            top: "10%",
            right: "-10%",
            width: "50vw",
            height: "50vw",
            background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 60%)`,
            filter: "blur(80px)",
          }}
        />

        {/* Secondary Glowing Orb */}
        <motion.div
          animate={{
            x: [0, -60, 50, 0],
            y: [0, 80, -50, 0],
            scale: [1, 1.1, 0.8, 1],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            position: "absolute",
            bottom: "-10%",
            left: "-10%",
            width: "45vw",
            height: "45vw",
            background: `radial-gradient(circle, ${theme.palette.secondary.main}12 0%, transparent 60%)`,
            filter: "blur(80px)",
          }}
        />

        {/* Tertiary Info Glowing Orb */}
        <motion.div
          animate={{
            x: [0, 40, -60, 0],
            y: [0, 40, -40, 0],
            scale: [1, 1.3, 0.9, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            position: "absolute",
            top: "40%",
            left: "30%",
            width: "35vw",
            height: "35vw",
            background: `radial-gradient(circle, ${theme.palette.info.main}08 0%, transparent 60%)`,
            filter: "blur(80px)",
          }}
        />
      </Box>

      {/* Main Content Area */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: "2.5rem",
                }}
              >
                Let's Build Something.
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", mb: 5, lineHeight: 1.8 }}
              >
                Whether you have a project in mind, need a frontend developer,
                or just want to chat about tech, I'm always open to discussing
                new opportunities.
              </Typography>
            </motion.div>

            <Stack spacing={2.5}>{contactItems}</Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              <Box>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.primary",
                      mb: 3,
                      fontWeight: 600,
                      letterSpacing: 1,
                    }}
                  >
                    FIND ME ON
                  </Typography>
                </motion.div>
                <Stack direction="row" flexWrap="wrap" gap={2}>
                  {socialItems}
                </Stack>
              </Box>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background:
                      "linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    borderTop: `2px solid ${theme.palette.primary.main}50`,
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.1)",
                      borderTop: `2px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 15px 40px -10px rgba(0,0,0,0.3)`,
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -20,
                      right: -20,
                      opacity: 0.1,
                    }}
                  >
                    <AutoAwesomeIcon
                      sx={{ fontSize: 100, color: "primary.main" }}
                    />
                  </Box>
                  <Stack spacing={2} position="relative" zIndex={1}>
                    <Typography
                      variant="h6"
                      sx={{ color: "text.primary", fontWeight: 600 }}
                    >
                      Quick Response Guaranteed
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.7 }}
                    >
                      I typically respond within <strong>12-24 hours</strong>.
                      For urgent matters or freelance inquiries, dropping a
                      message on Telegram is the fastest way to reach me.
                    </Typography>
                  </Stack>
                </Paper>
              </motion.div>
            </Stack>
          </Grid>
        </Grid>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              mt: { xs: 8, md: 12 },
              color: "text.disabled",
              fontWeight: 500,
              letterSpacing: 1,
            }}
          >
            © {new Date().getFullYear()} Pouriya Sabouri. Designed with passion.
          </Typography>
        </motion.div>
      </Container>
    </Box>
  );
}
